/**
 * NocturneGuide — what to look at, from where, when and with which telescope. Holds the observing
 * catalogue (planets, Moon, deep sky) with plain-language advice and the aperture each needs, the
 * visitor's place (kept in this browser only), a night's programme and the upcoming telescope events
 * (Moon–planet meetings, oppositions, first quarter, moonless night). Built on NocturneEphem.
 */
(() => {
  if (window.NocturneGuide || !window.NocturneEphem) return;
  const E = window.NocturneEphem;

  const TZ = 'Europe/Paris';
  const HOUR = 3600000;
  const DAY = 24 * HOUR;
  const DEFAULT_PLACE = { name: 'Paris', lat: 48.857, lon: 2.352 };

  // rec: the telescopes we suggest first (product handles), best value for this target.
  const OBJECTS = {
    saturn: { name: 'Saturne', kind: 'planet', color: '#ecd9a0', minAp: 60, priority: 10, rec: ['blitz-114-base', 'kelvin-nova-plus-70-700'], short: 'ses anneaux', see: 'Ses anneaux, bien visibles dès 60 mm : le « waouh » du premier soir.' },
    jupiter: { name: 'Jupiter', kind: 'planet', color: '#f6e6c8', minAp: 60, priority: 9, rec: ['blitz-114-base', 'kelvin-nova-plus-70-700'], short: 'ses bandes nuageuses et ses quatre lunes', see: 'Ses bandes nuageuses et ses quatre lunes, qui changent de place d’heure en heure.' },
    moon: { name: 'La Lune', kind: 'moon', minAp: 50, priority: 8, rec: ['kelvin-nova-base-70-400'] },
    mars: { name: 'Mars', kind: 'planet', color: '#ff9c6e', minAp: 90, priority: 6, rec: ['blitz-114-base'], short: 'son disque orangé', see: 'La planète rouge : sa calotte polaire et ses zones sombres, surtout quand elle passe au plus près.' },
    venus: { name: 'Vénus', kind: 'planet', color: '#fffbe6', minAp: 50, priority: 5, rec: ['kelvin-nova-base-70-400'], short: 'sa phase, comme une petite Lune', see: 'Au télescope, elle montre des phases, comme une petite Lune.' },
    mercury: { name: 'Mercure', kind: 'planet', color: '#d9c4b5', minAp: 50, priority: 3, rec: ['kelvin-nova-base-70-400'], see: 'Rare et discrète : un minuscule croissant, bas dans les lueurs du crépuscule.' },
    m42: { name: 'La nébuleuse d’Orion', kind: 'nebula', ra: 83.82, dec: -5.39, minAp: 70, priority: 7, rec: ['kelvin-nova-plus-114-500'], see: 'Un nuage de gaz éclairé par de jeunes étoiles, juste sous la ceinture d’Orion.' },
    m31: { name: 'La galaxie d’Andromède', kind: 'galaxy', ra: 10.68, dec: 41.27, minAp: 70, priority: 6, rec: ['kelvin-nova-plus-130-650'], see: 'Une autre galaxie, à 2,5 millions d’années-lumière : un ovale laiteux, d’autant plus grand que le miroir est large.' },
    m45: { name: 'Les Pléiades', kind: 'cluster', ra: 56.85, dec: 24.12, minAp: 50, priority: 5, rec: ['kelvin-nova-base-70-400', 'skyline-travel-80'], see: 'Des dizaines d’étoiles bleutées dans le champ : magnifique à faible grossissement.' },
    m13: { name: 'L’amas d’Hercule', kind: 'cluster', ra: 250.42, dec: 36.46, minAp: 114, priority: 4, rec: ['kelvin-nova-plus-130-650'], see: 'Des centaines de milliers d’étoiles en boule ; dès 114 mm, ses bords se séparent en points.' },
  };
  const PLANETS = ['saturn', 'jupiter', 'mars', 'venus', 'mercury'];
  const DIRS = ['au nord', 'au nord-est', 'à l’est', 'au sud-est', 'au sud', 'au sud-ouest', 'à l’ouest', 'au nord-ouest'];

  const clock = new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  const longDay = new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });
  const weekdayFmt = new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, weekday: 'long' });
  const fmt = (t) => clock.format(t).replace(':', '\u00a0h\u00a0').replace(/^0/, '');
  const day = (t) => longDay.format(t).replace(/ 1 /, ' 1er ');
  const dir = (az) => (az > 165 && az < 195 ? 'plein sud' : DIRS[Math.round(az / 45) % 8]);

  // Height in fists held at arm's length (about 10° each): the beginner's way to find a spot in the sky.
  function where(alt, az) {
    const k = Math.round(alt / 10);
    const height = alt < 8 ? 'juste au-dessus de l’horizon' : alt > 75 ? 'presque au-dessus de votre tête' : `à ${k} poing${k > 1 ? 's' : ''} au-dessus de l’horizon`;
    return `${dir(az)}, ${height}`;
  }

  function moonName({ illum, waxing }) {
    if (illum < 0.03) return 'Nouvelle Lune';
    if (illum > 0.97) return 'Pleine Lune';
    if (Math.abs(illum - 0.5) < 0.07) return waxing ? 'Premier quartier' : 'Dernier quartier';
    if (illum < 0.5) return waxing ? 'Premier croissant' : 'Dernier croissant';
    return waxing ? 'Gibbeuse croissante' : 'Gibbeuse décroissante';
  }

  function moonSee({ illum }) {
    if (illum > 0.95) return 'Pleine et éblouissante : peu de relief, mais le filtre lunaire fait ressortir les mers et les traînées claires.';
    if (illum < 0.35) return 'Un croissant : les montagnes projettent de longues ombres. Le plus beau à photographier au smartphone.';
    return 'Les cratères ressortent le long de la ligne d’ombre, là où le Soleil se lève sur eux.';
  }

  function loadPlace() {
    try {
      const p = JSON.parse(localStorage.getItem('nx-place'));
      if (p && Number.isFinite(p.lat) && Number.isFinite(p.lon) && p.name) return p;
    } catch (e) {}
    return { ...DEFAULT_PLACE, isDefault: true };
  }

  function savePlace(p) {
    try { localStorage.setItem('nx-place', JSON.stringify({ name: p.name, sub: p.sub || '', lat: p.lat, lon: p.lon })); } catch (e) {}
  }

  // A time inside the night that starts `offset` evenings from now (0 = tonight, or the night in progress).
  const nightTime = (offset, now = Date.now()) => (offset === 0 ? now : E.localNoon(now, TZ) + offset * DAY + 6 * HOUR);

  function nightLabel(offset, now = Date.now()) {
    if (offset === 0) return 'ce soir';
    if (offset === 1) return 'demain soir';
    return `${weekdayFmt.format(nightTime(offset, now))} soir`;
  }

  /** One night's programme for a place: Sun times, what is worth seeing (in order), the star of the night. */
  function night(place, offset = 0, now = Date.now()) {
    const ids = Object.keys(OBJECTS);
    const n = E.night(nightTime(offset, now), place.lat, place.lon, ids.map((id) => (OBJECTS[id].ra === undefined ? id : { id, eq: OBJECTS[id] })));
    const sky = E.sky(n.start + 12 * HOUR);
    const phase = sky.phase;
    const dusk = n.dusk ?? n.sunset;
    const dawn = n.dawn ?? n.sunrise;
    const items = [];
    const missed = [];
    for (const id of ids) {
      const o = OBJECTS[id];
      const b = n.bodies[id];
      if (!b.best || (id === 'moon' && phase.illum < 0.05)) {
        if (o.kind === 'planet' && id !== 'mercury') missed.push(`${o.name} (${E.separation(sky.sun, sky[id]) < 25 ? 'trop près du Soleil' : 'sous l’horizon la nuit'})`);
        continue;
      }
      if (o.kind !== 'planet' && o.kind !== 'moon' && b.best.alt < 30) continue;
      // Suggest the evening moment when the highest point comes after 1 a.m.
      const late = b.best.t > n.start + 13 * HOUR;
      const at = late && b.good && b.good.t < b.best.t ? b.good : b.best;
      const start = id === 'moon' && b.rise > n.sunset && b.rise < n.sunrise ? `Se lève à ${fmt(b.rise)}` : b.from - dusk < 20 * 60000 ? 'Dès la nuit tombée' : `À partir de ${fmt(b.from)}`;
      const end = dawn - b.to < 20 * 60000 ? 'jusqu’à l’aube' : `jusqu’à ${fmt(b.to)}`;
      const item = {
        id,
        ...o,
        b,
        at,
        atLabel: at === b.best ? 'Au plus haut à' : 'À voir dès',
        range: `${start}, ${end}`,
        where: where(at.alt, at.az),
        score: o.priority + (at.t < n.start + 13 * HOUR ? 3 : 0) + (b.best.alt > 30 ? 1 : 0),
      };
      if (id === 'moon') {
        item.meta = `${moonName(phase)} · ${Math.round(phase.illum * 100)} %`;
        item.see = moonSee(phase);
        if (phase.illum > 0.95) item.score -= 4;
      }
      items.push(item);
    }
    // Deep sky: only the two best placed, so the list stays short.
    const deep = items.filter((i) => !['planet', 'moon'].includes(i.kind)).sort((a, c) => c.b.best.alt - a.b.best.alt).slice(2);
    const list = items.filter((i) => !deep.includes(i)).sort((a, c) => a.at.t - c.at.t);
    const star = [...list].sort((a, c) => c.score - a.score)[0] || null;
    const toNew = ((1 - phase.frac) % 1) * 29.530588853;
    return { n, phase, items: list, star, missed, toNewMoon: toNew };
  }

  /** The telescope we suggest for an object: a curated pick if on sale, else the cheapest that can show it. */
  function recommend(id, products) {
    const o = OBJECTS[id];
    if (!o || !products?.length) return null;
    const ok = products.filter((p) => p.available && !p.smart && p.aperture >= o.minAp);
    for (const h of o.rec) {
      const p = ok.find((x) => x.handle === h);
      if (p) return p;
    }
    return ok.sort((a, c) => a.cents - c.cents)[0] || null;
  }

  /** Upcoming events worth a telescope, for a place: Moon–planet meetings, oppositions, Moon phases. */
  function events(place, now = Date.now()) {
    const { lat, lon } = place;
    const noon = E.localNoon(now, TZ);
    const evening = (t) => E.localNoon(t, TZ) + 10 * HOUR; // 22 h on the evening that night belongs to
    const out = [];

    // First quarter (best for craters) and new Moon (darkest sky), stepping 3 h.
    let prev = E.sky(now).phase.frac;
    let quarter = null, newMoon = null;
    for (let t = now + 3 * HOUR; t < now + 32 * DAY && (!quarter || !newMoon); t += 3 * HOUR) {
      const f = E.sky(t).phase.frac;
      if (!quarter && prev < 0.25 && f >= 0.25) quarter = t;
      if (!newMoon && f < prev) newMoon = t;
      prev = f;
    }
    if (quarter) out.push({ kind: 'quarter', id: 'moon', t: evening(quarter), title: 'Premier quartier de Lune', lead: `Le soir du ${day(evening(quarter))}`, text: 'Le meilleur soir du mois pour les cratères : ils s’alignent le long de la ligne d’ombre, avec un relief maximal.' });
    if (newMoon) out.push({ kind: 'newmoon', id: 'm31', t: evening(newMoon), title: 'Nuit sans Lune', lead: `La nuit du ${day(evening(newMoon))}`, text: 'Le ciel le plus noir du mois : c’est le moment des galaxies et des nébuleuses, loin des lumières de la ville.' });

    // The Moon passing a planet, checked at 21 h, 23 h and 5 h when both are well up in a dark sky.
    const meets = [];
    for (let k = 0; k < 45; k++) {
      for (const h of [9, 11, 17]) {
        const t = noon + k * DAY + h * HOUR;
        if (t < now - HOUR) continue;
        const s = E.sky(t);
        if (E.position('sun', t, lat, lon, s).alt > -8 || E.position('moon', t, lat, lon, s).alt < 8) continue;
        for (const id of ['saturn', 'jupiter', 'mars', 'venus']) {
          const sep = E.separation(s.moon, s[id]);
          if (sep < 8 && E.position(id, t, lat, lon, s).alt > 8) meets.push({ id, t, sep, morning: h === 17 });
        }
      }
    }
    for (const id of ['saturn', 'jupiter', 'mars', 'venus']) {
      // One event per pass (passes are about a month apart): the closest moment.
      const mine = meets.filter((m) => m.id === id).sort((a, c) => a.t - c.t);
      const groups = [];
      for (const m of mine) {
        const g = groups[groups.length - 1];
        if (g && m.t - g[g.length - 1].t < 4 * DAY) g.push(m);
        else groups.push([m]);
      }
      for (const g of groups) {
        const m = g.reduce((a, c) => (c.sep < a.sep ? c : a));
        const o = OBJECTS[id];
        out.push({
          kind: 'meet',
          id,
          t: m.t,
          title: `La Lune rejoint ${o.name}`,
          lead: `${m.morning ? 'Le matin' : 'Le soir'} du ${day(m.t)}, vers ${fmt(m.t)}`,
          text: `La Lune passe à ${Math.max(1, Math.round(m.sep))}° de ${o.name}, moins d’un poing à côté : elle vous montre où pointer. Puis, au télescope, ${o.short}.`,
          evening: !m.morning,
        });
      }
    }

    // Oppositions: the planet faces the Sun, visible all night, at its biggest and brightest.
    const OPPOSITION = {
      jupiter: 'Visible toute la nuit, plus grosse et plus brillante que le reste de l’année : le meilleur moment pour ses bandes et ses lunes.',
      saturn: 'Visible toute la nuit et au plus brillant : le meilleur moment de l’année pour ses anneaux.',
      mars: 'Cela n’arrive que tous les deux ans environ : le meilleur moment pour deviner sa calotte polaire et ses zones sombres, dès 90 mm.',
    };
    for (const id of Object.keys(OPPOSITION)) {
      let last = -1, rising = false;
      for (let k = 0; k < 300; k++) {
        const t = noon + k * DAY;
        const s = E.sky(t);
        const el = E.separation(s.sun, s[id]);
        if (rising && el < last && last > 165) {
          const tt = evening(t - DAY);
          out.push({ kind: 'opposition', id, t: tt, title: `${OBJECTS[id].name} au plus près`, lead: `À partir du ${day(tt)}`, text: OPPOSITION[id] });
          break;
        }
        rising = el > last;
        last = el;
      }
    }
    return out.filter((e) => e.t > now - 2 * HOUR).sort((a, c) => a.t - c.t);
  }

  /** An .ics reminder for an event (one hour before), built in the browser. */
  function ics(ev) {
    const stamp = (t) => new Date(t).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const esc = (s) => s.replace(/([,;\\])/g, '\\$1').replace(/\n/g, ' ');
    return [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Nocturne//Ciel de ce soir//FR', 'BEGIN:VEVENT',
      `UID:${ev.kind}-${ev.id}-${ev.t}@nocturne`, `DTSTAMP:${stamp(Date.now())}`, `DTSTART:${stamp(ev.t)}`, `DTEND:${stamp(ev.t + HOUR)}`,
      `SUMMARY:${esc(`${ev.title} · Nocturne`)}`, `DESCRIPTION:${esc(ev.text)}`,
      'BEGIN:VALARM', 'TRIGGER:-PT1H', 'ACTION:DISPLAY', `DESCRIPTION:${esc(ev.title)}`, 'END:VALARM',
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
  }

  window.NocturneGuide = { OBJECTS, PLANETS, fmt, day, dir, where, moonName, loadPlace, savePlace, nightTime, nightLabel, night, recommend, events, ics };
})();
